import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CheckCircle2, CircleAlert, Clock } from "lucide-react";
import type { CurriculumProgressSummary } from "@/hooks/useLearnerCurriculumProgress";

interface Props {
  summary: CurriculumProgressSummary;
  /** Hide the curriculum title when the surrounding card already shows it. */
  showTitle?: boolean;
  className?: string;
}

/**
 * Compact progress summary, used on the dashboard and on curriculum cards.
 *
 * For a cohort learner the ASSIGNED figure leads, because that is what they are
 * accountable for right now; overall curriculum progress is shown underneath as
 * context. For a self-paced learner there is no distinction, so only the overall
 * figure is shown.
 */
export function CurriculumProgressSummaryCard({
  summary,
  showTitle = true,
  className,
}: Props) {
  const {
    curriculumTitle,
    totalModules,
    completedModules,
    overallPercent,
    assignedModules,
    assignedCompleted,
    assignedPercent,
    isCohortGated,
    outstandingModules,
    readyToComplete,
  } = summary;

  const isDone = completedModules === totalModules && totalModules > 0;
  // For cohort learners the headline tracks assigned work, not the whole programme.
  const headlinePercent = isCohortGated ? assignedPercent : overallPercent;

  return (
    <div className={cn("w-full space-y-2", className)}>
      {showTitle && (
        <div className="font-medium text-sm">{curriculumTitle}</div>
      )}

      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted-foreground">
          {isCohortGated ? "Assigned to you" : "Progress"}
        </span>
        <span className="font-semibold tabular-nums">{headlinePercent}%</span>
      </div>

      <Progress value={headlinePercent} className="h-2" />

      <div className="text-xs text-muted-foreground">
        {isCohortGated ? (
          <>
            {assignedCompleted} of {assignedModules}{" "}
            {assignedModules === 1 ? "module" : "modules"} assigned to you
            complete
            {assignedModules < totalModules && (
              <>
                {" · "}
                {completedModules} of {totalModules} in the full programme
              </>
            )}
          </>
        ) : (
          <>
            {completedModules} of {totalModules}{" "}
            {totalModules === 1 ? "module" : "modules"} complete
          </>
        )}
      </div>

      {/* Actionable states, most urgent first. */}
      <div className="flex flex-wrap gap-1.5 pt-0.5">
        {isDone && (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Programme complete
          </Badge>
        )}

        {readyToComplete > 0 && (
          <Badge variant="default" className="gap-1">
            <CircleAlert className="h-3 w-3" />
            {readyToComplete === 1
              ? "1 module ready to mark complete"
              : `${readyToComplete} modules ready to mark complete`}
          </Badge>
        )}

        {!isDone && outstandingModules > 0 && (
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {outstandingModules === 1
              ? "1 module still to do"
              : `${outstandingModules} modules still to do`}
          </Badge>
        )}

        {!isDone && outstandingModules === 0 && readyToComplete === 0 && (
          <Badge variant="outline" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            You're up to date
          </Badge>
        )}
      </div>
    </div>
  );
}
